import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { menuItemss } from "./SideMenu";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FiscalYearContext } from "../FiscalYear/FiscalYearContext";
import getCompanyPack from "../Auth/GetCompanyPack";
import authHeader from "../Auth/authHeader";
import logo from "../Images/only_logo.png";
import text from "../Images/only_slogan.png";
import "./NewSidebar.scss";

const createPageUrl = (path) => path;

export default function NewSidebar({
  isOpen,
  isMobile,
  onCloseMobile,
  userRole = "user",
  onExpand,
  searchTerm = "",
}) {
  const { pathname } = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [searchMenuOverrides, setSearchMenuOverrides] = useState({});
  const previousHasSearchRef = useRef(false);
  const [pack, setPack] = useState("");

  // Use FiscalYearContext
  const { fiscalYear, setFiscalYear } = useContext(FiscalYearContext);

  // Get auth headers
  const headers = authHeader();

  // Fetch company package
  useEffect(() => {
    getCompanyPack(headers).then(setPack);
  }, []);

  // Handle fiscal year change
  const handleFiscalYearChange = (event) => {
    setFiscalYear(Number(event.target.value));
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const hasSearch = normalizedSearch.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setOpenMenus({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!hasSearch) {
      setSearchMenuOverrides({});
    }
  }, [hasSearch]);

  useEffect(() => {
    const wasSearching = previousHasSearchRef.current;

    // Auto-open only when search starts; do not force reopen after manual close.
    if (!wasSearching && hasSearch && !isOpen && onExpand) {
      onExpand();
    }

    previousHasSearchRef.current = hasSearch;
  }, [hasSearch, isOpen, onExpand]);

  const sidebarWidth = isOpen ? "sidebar-expanded" : "sidebar-collapsed";
  const mobileClasses = isMobile
    ? `sidebar-mobile ${isOpen ? "sidebar-mobile-open" : "sidebar-mobile-closed"}`
    : `sidebar-desktop ${sidebarWidth} sidebar-hover-expand`;

  // Overlay for mobile
  const overlay = isMobile && isOpen ? (
    <div className="sidebar-overlay" onClick={onCloseMobile} />
  ) : null;

  const toggleMenu = (id, currentExpanded) => {
    if (hasSearch) {
      setSearchMenuOverrides((prev) => ({
        ...prev,
        [id]: !currentExpanded,
      }));
      return;
    }

    setOpenMenus((prev) => {
      if (prev[id]) {
        // closing
        return {};
      } else {
        // opening
        if (!isOpen && onExpand) {
          onExpand();
        }
        return { [id]: true };
      }
    });
  };

  const filteredMenuItems = menuItemss
    .filter((item) => item.pack.includes(pack))
    .filter((item) => item.role.includes(userRole))
    .map((item) => {
      if (!item.subMenus || item.subMenus.length === 0) {
        const menuMatches = !hasSearch
          ? true
          : item.name.toLowerCase().includes(normalizedSearch);
        return menuMatches ? item : null;
      }

      const packageFilteredSubMenus = item.subMenus.filter((subMenu) =>
        subMenu.pack.includes(pack)
      );

      const matchingSubMenus = !hasSearch
        ? packageFilteredSubMenus
        : packageFilteredSubMenus.filter((subMenu) =>
            subMenu.name.toLowerCase().includes(normalizedSearch)
          );

      const menuMatches = !hasSearch
        ? true
        : item.name.toLowerCase().includes(normalizedSearch);

      if (!menuMatches && matchingSubMenus.length === 0) return null;

      return {
        ...item,
        subMenus: hasSearch && !menuMatches ? matchingSubMenus : packageFilteredSubMenus,
        hasMatchingSubMenus: matchingSubMenus.length > 0,
      };
    })
    .filter(Boolean);

  return (
    <>
      {overlay}
      <aside className={mobileClasses}>
        {isMobile && (
          <div className="sidebar-mobile-header">
            <Link to="/dashboard" className="sidebar-brand-link sidebar-brand-link-mobile">
              <img src={logo} alt="Accountree logo" className="sidebar-brand-logo" />
              <img src={text} alt="Accountree" className="sidebar-brand-text sidebar-brand-text-mobile" />
            </Link>
          </div>
        )}

        {!isMobile && (
          <div className="sidebar-desktop-header">
            <Link to="/dashboard" className="sidebar-brand-link">
              <img src={logo} alt="Accountree logo" className="sidebar-brand-logo" />
              <img src={text} alt="Accountree" className="sidebar-brand-text" />
            </Link>
          </div>
        )}

        <div className="sidebar-menu-container">
          {filteredMenuItems.map((item) => {

            const hasSubMenus = item.subMenus && item.subMenus.length > 0;

            if (hasSubMenus) {
              const isExpanded = hasSearch
                ? (searchMenuOverrides[item.name] ?? item.hasMatchingSubMenus)
                : openMenus[item.name];
              const isChildActive = item.subMenus.some((child) => {
                const childUrl = createPageUrl(child.to);
                return (
                  pathname === childUrl ||
                  pathname.startsWith(childUrl + "/")
                );
              });

              return (
                <div key={item.name} className="sidebar-menu-group">
                  <div className="sidebar-menu-items">
                    <div className="sidebar-menu-item-wrapper">
                      <button
                        onClick={() => toggleMenu(item.name, isExpanded)}
                        className={`sidebar-menu-item sidebar-collapse-btn ${
                          isChildActive || isExpanded
                            ? "sidebar-active"
                            : "sidebar-inactive"
                        }`}
                      >
                        <div className="sidebar-item-left">
                          <i className={item.iconClassName + " sidebar-icon"} />
                          <span className="sidebar-item-text">
                            {item.name}
                          </span>
                        </div>
                        <div className="sidebar-item-arrow">
                          {isExpanded ? (
                            <ChevronDown className="sidebar-arrow-icon" />
                          ) : (
                            <ChevronRight className="sidebar-arrow-icon" />
                          )}
                        </div>
                      </button>

                      {/* Submenu Items */}
                      {isExpanded && (
                        <div className="sidebar-submenu">
                          {item.subMenus.map((child) => {
                            const subMenuIconClass =
                              child.iconClassName ||
                              child.iconClass ||
                              child.icon ||
                              "bi bi-dot";
                            const childUrl = createPageUrl(child.to);
                            const isChildActiveItem =
                              pathname === childUrl ||
                              pathname.startsWith(childUrl + "/");

                            return (
                              <Link
                                key={child.name}
                                to={childUrl}
                                className={`sidebar-submenu-item ${
                                  isChildActiveItem
                                    ? "sidebar-submenu-active"
                                    : "sidebar-submenu-inactive"
                                }`}
                              >
                                <i className={subMenuIconClass + " sidebar-submenu-icon"} />
                                <span className="sidebar-submenu-text">
                                  {child.name}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            const url = createPageUrl(item.to);
            const isActive =
              pathname === url || pathname.startsWith(url + "/");

            return (
              <>
                <div key={item.name} className="sidebar-menu-group">
                  <div className="sidebar-menu-items">
                    <div className="sidebar-menu-item-wrapper">
                      <Link
                        to={url}
                        className={`sidebar-menu-item sidebar-link ${
                          isActive ? "sidebar-active" : "sidebar-inactive"
                        }`}
                      >
                        {isActive && (
                          <div className="sidebar-active-indicator" />
                        )}

                        <div className="sidebar-item-left">
                          <i className={item.iconClassName + " sidebar-icon"} />

                          <span className="sidebar-item-text">
                            {item.name}
                          </span>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
        </div>

        {/* Sidebar Docked Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-content">
            {/* Fiscal Year Select */}
            <div className="sidebar-fiscal-year">
              <label>Fiscal Year</label>
              <select
                className="sidebar-select"
                value={fiscalYear}
                onChange={handleFiscalYearChange}
              >
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
                <option value={2024}>2024</option>
                <option value={2023}>2023</option>
                <option value={2022}>2022</option>
              </select>
            </div>

            {/* Package, Version & Copyright */}
            <div className="sidebar-package-info">
              <div className="sidebar-badge">
                <span style={{ marginRight: "4px" }}>★</span>
                Package: {pack || "Loading..."}
              </div>
              <div className="sidebar-copyright">
                <p>© 2026 Accountree | Version: v1.0.0</p>
              </div>
            </div>
          </div>

          {/* Small icon version when collapsed */}
          {!isMobile && !isOpen && (
            <div className="sidebar-collapsed-badge">
              <div className="sidebar-mini-badge" title={`Package: ${pack || "Loading..."} | Fiscal Year ${fiscalYear}-${fiscalYear + 1}`}>
                {pack ? pack.charAt(0) : "?"}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}